// src/components/payroll/employees/EmployeePayrollForm.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { AppContext } from '../../../context/AppContext';
import { db } from '../../../firebase/config';
import {
  doc, getDoc, setDoc, collection, query, where, getDocs,
  updateDoc, serverTimestamp
} from 'firebase/firestore';
import {
  ChevronLeft, User, AlertCircle, X, Plus, DollarSign, Check
} from 'lucide-react';

const EmployeePayrollForm = ({ isEditing = false }) => {
  const navigate = useNavigate();
  const { payrollId } = useParams();
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    employeeId: '',
    salaryType: 'monthly',
    baseSalary: '',
    taxIdentifier: '',
    taxBracket: 'standard',
    paymentMethod: 'bankTransfer',
    bankDetails: {
      bankName: '',
      accountNumber: '',
      routingNumber: '',
      accountHolderName: ''
    },
    customDeductions: [],
    customBenefits: [],
    notes: ''
  });
  
  // New deduction/benefit item
  const [newItem, setNewItem] = useState({
    type: 'deduction',
    name: '',
    value: '',
    valueType: 'percentage',
    description: ''
  });
  
  // Fetch employee list and existing payroll details if editing
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !activeBusiness) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch employees that don't have payroll details yet
        const teamMembersQuery = query(
          collection(db, 'teamMembers'),
          where('businessId', '==', activeBusiness.id)
        );
        
        const existingPayrollQuery = query(
          collection(db, 'employeePayrollDetails'),
          where('businessId', '==', activeBusiness.id)
        );
        
        const [teamSnapshot, payrollSnapshot] = await Promise.all([
          getDocs(teamMembersQuery),
          getDocs(existingPayrollQuery)
        ]);
        
        // Get IDs of employees that already have payroll details
        const employeesWithPayroll = new Set();
        payrollSnapshot.forEach(doc => {
          const data = doc.data();
          employeesWithPayroll.add(data.employeeId);
        });
        
        // Build employees list
        const employeesList = [];
        teamSnapshot.forEach(doc => {
          const data = doc.data();
          // Only include employees without payroll details for new entries
          if (!isEditing && employeesWithPayroll.has(doc.id)) {
            return;
          }
          
          employeesList.push({
            id: doc.id,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            position: data.position || 'Employee'
          });
        });
        
        setEmployees(employeesList);
        
        // If editing, fetch existing payroll details
        if (isEditing && payrollId) {
          const payrollDoc = await getDoc(doc(db, 'employeePayrollDetails', payrollId));
          
          if (!payrollDoc.exists()) {
            setError('Payroll details not found');
            setLoading(false);
            return;
          }
          
          const payrollData = payrollDoc.data();
          
          // Format data for form
          setFormData({
            employeeId: payrollData.employeeId || '',
            salaryType: payrollData.salaryType || 'monthly',
            baseSalary: payrollData.baseSalary || '',
            taxIdentifier: payrollData.taxIdentifier || '',
            taxBracket: payrollData.taxBracket || 'standard',
            paymentMethod: payrollData.paymentMethod || 'bankTransfer',
            bankDetails: payrollData.bankDetails || {
              bankName: '',
              accountNumber: '',
              routingNumber: '',
              accountHolderName: ''
            },
            customDeductions: payrollData.customDeductions || [],
            customBenefits: payrollData.customBenefits || [],
            notes: payrollData.notes || ''
          });
        }
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentUser, activeBusiness, isEditing, payrollId]);
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('bankDetails.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        bankDetails: {
          ...prev.bankDetails,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  // Handle new deduction/benefit input changes
  const handleNewItemChange = (e) => {
    const { name, value } = e.target;
    setNewItem(prev => ({ ...prev, [name]: value }));
  };
  
  // Add new deduction/benefit
  const handleAddItem = () => {
    if (!newItem.name || !newItem.value) return;
    
    const newItemObj = {
      name: newItem.name,
      type: newItem.valueType,
      value: parseFloat(newItem.value),
      description: newItem.description
    };
    
    if (newItem.type === 'deduction') {
      setFormData(prev => ({
        ...prev,
        customDeductions: [...prev.customDeductions, newItemObj]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        customBenefits: [...prev.customBenefits, newItemObj]
      }));
    }
    
    // Reset form
    setNewItem({
      type: 'deduction',
      name: '',
      value: '',
      valueType: 'percentage',
      description: ''
    });
  };
  
  // Remove deduction/benefit
  const handleRemoveItem = (type, index) => {
    if (type === 'deduction') {
      setFormData(prev => ({
        ...prev,
        customDeductions: prev.customDeductions.filter((_, i) => i !== index)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        customBenefits: prev.customBenefits.filter((_, i) => i !== index)
      }));
    }
  };
  
  // Validate form before submission
  const validateForm = () => {
    const errors = {};
    
    if (!formData.employeeId) {
      errors.employeeId = 'Please select an employee';
    }
    
    if (!formData.baseSalary || parseFloat(formData.baseSalary) <= 0) {
      errors.baseSalary = 'Please enter a valid salary amount';
    }
    
    if (formData.paymentMethod === 'bankTransfer') {
      if (!formData.bankDetails.bankName) errors.bankName = 'Bank name is required';
      if (!formData.bankDetails.accountNumber) errors.accountNumber = 'Account number is required';
    }
    
    return errors;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      // Show first error
      alert(Object.values(validationErrors)[0]);
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      const payrollData = {
        ...formData,
        businessId: activeBusiness.id,
        baseSalary: parseFloat(formData.baseSalary),
        updatedAt: serverTimestamp()
      };
      
      if (isEditing) {
        // Update existing record
        await updateDoc(doc(db, 'employeePayrollDetails', payrollId), payrollData);
      } else {
        // Create new record
        payrollData.createdAt = serverTimestamp();
        payrollData.createdBy = currentUser.uid;
        
        // Check if employee already has payroll details
        // Check if employee already has payroll details
        const existingQuery = query(
            collection(db, 'employeePayrollDetails'),
            where('employeeId', '==', formData.employeeId),
            where('businessId', '==', activeBusiness.id)
          );
          
          const existingDocs = await getDocs(existingQuery);
          
          if (!existingDocs.empty) {
            setError('This employee already has payroll details set up');
            setSubmitting(false);
            return;
          }
          
          await setDoc(doc(collection(db, 'employeePayrollDetails')), payrollData);
        }
        
        // Navigate back to employee list
        navigate('/payroll/employees');
        
      } catch (err) {
        console.error('Error saving payroll details:', err);
        setError('Failed to save payroll details. Please try again.');
      } finally {
        setSubmitting(false);
      }
    };
    
    if (loading) {
      return (
        <div className="bg-white rounded-xl shadow-md p-6 flex justify-center items-center min-h-[300px]">
          <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
        </div>
      );
    }
    
    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <button
            onClick={() => navigate('/payroll/employees')}
            className="text-[#190192] hover:text-[#2C0DB5] flex items-center"
          >
            <ChevronLeft size={16} className="mr-1" />
            Back to Employees
          </button>
          <h1 className="mt-2 text-xl font-bold text-gray-900">
            {isEditing ? 'Edit Employee Payroll' : 'Add Employee Payroll'}
          </h1>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 rounded-lg text-red-700 flex items-center">
              <AlertCircle size={20} className="mr-2" />
              <span>{error}</span>
            </div>
          )}
          
          <div className="space-y-8">
            {/* Employee Selection */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Employee Information</h2>
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="employeeId" className="block text-sm font-medium text-gray-700 mb-1">
                      Select Employee <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="employeeId"
                      name="employeeId"
                      value={formData.employeeId}
                      onChange={handleInputChange}
                      disabled={isEditing}
                      className={`block w-full rounded-lg border ${
                        isEditing ? 'bg-gray-100' : 'border-gray-300'
                      } px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20`}
                      required
                    >
                      <option value="">-- Select Employee --</option>
                      {employees.map(employee => (
                        <option key={employee.id} value={employee.id}>
                          {employee.firstName} {employee.lastName} - {employee.position}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="taxIdentifier" className="block text-sm font-medium text-gray-700 mb-1">
                      Tax Identifier (SSN/TIN)
                    </label>
                    <input
                      type="text"
                      id="taxIdentifier"
                      name="taxIdentifier"
                      value={formData.taxIdentifier}
                      onChange={handleInputChange}
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                      placeholder="XXX-XX-XXXX"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Salary Information */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Salary Information</h2>
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label htmlFor="salaryType" className="block text-sm font-medium text-gray-700 mb-1">
                      Salary Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="salaryType"
                      name="salaryType"
                      value={formData.salaryType}
                      onChange={handleInputChange}
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                      required
                    >
                      <option value="hourly">Hourly</option>
                      <option value="monthly">Monthly</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="baseSalary" className="block text-sm font-medium text-gray-700 mb-1">
                      Base Salary <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <DollarSign size={16} className="text-gray-400" />
                      </div>
                      <input
                        type="number"
                        id="baseSalary"
                        name="baseSalary"
                        value={formData.baseSalary}
                        onChange={handleInputChange}
                        min="0"
                        step={formData.salaryType === 'hourly' ? "0.01" : "1"}
                        className="pl-10 block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                        placeholder={formData.salaryType === 'hourly' ? "25.00" : "5000"}
                        required
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {formData.salaryType === 'hourly' 
                        ? 'Amount per hour'
                        : formData.salaryType === 'annual'
                          ? 'Annual salary'
                          : 'Monthly salary'
                      }
                    </p>
                  </div>
                  
                  <div>
                    <label htmlFor="taxBracket" className="block text-sm font-medium text-gray-700 mb-1">
                      Tax Bracket
                    </label>
                    <select
                      id="taxBracket"
                      name="taxBracket"
                      value={formData.taxBracket}
                      onChange={handleInputChange}
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                    >
                      <option value="standard">Standard</option>
                      <option value="reduced">Reduced Rate</option>
                      <option value="high">Higher Rate</option>
                      <option value="exempt">Tax Exempt</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Payment Information */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Payment Information</h2>
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <div>
                  <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Method
                  </label>
                  <select
                    id="paymentMethod"
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleInputChange}
                    className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                  >
                    <option value="bankTransfer">Bank Transfer</option>
                    <option value="check">Check</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>
                
                {formData.paymentMethod === 'bankTransfer' && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-1">
                        Bank Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="bankName"
                        name="bankDetails.bankName"
                        value={formData.bankDetails.bankName}
                        onChange={handleInputChange}
                        className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="accountHolderName" className="block text-sm font-medium text-gray-700 mb-1">
                        Account Holder Name
                      </label>
                      <input
                        type="text"
                        id="accountHolderName"
                        name="bankDetails.accountHolderName"
                        value={formData.bankDetails.accountHolderName}
                        onChange={handleInputChange}
                        className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700 mb-1">
                        Account Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="accountNumber"
                        name="bankDetails.accountNumber"
                        value={formData.bankDetails.accountNumber}
                        onChange={handleInputChange}
                        className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="routingNumber" className="block text-sm font-medium text-gray-700 mb-1">
                        Routing Number
                      </label>
                      <input
                        type="text"
                        id="routingNumber"
                        name="bankDetails.routingNumber"
                        value={formData.bankDetails.routingNumber}
                        onChange={handleInputChange}
                        className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Deductions & Benefits */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Custom Deductions & Benefits</h2>
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <div className="mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="md:col-span-1">
                      <label htmlFor="itemType" className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        id="itemType"
                        name="type"
                        value={newItem.type}
                        onChange={handleNewItemChange}
                        className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                      >
                        <option value="deduction">Deduction</option>
                        <option value="benefit">Benefit</option>
                      </select>
                    </div>
                    
                    <div className="md:col-span-1">
                      <label htmlFor="itemName" className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        id="itemName"
                        name="name"
                        value={newItem.name}
                        onChange={handleNewItemChange}
                        className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                        placeholder="e.g., Healthcare"
                      />
                    </div>
                    
                    <div className="md:col-span-1">
                      <label htmlFor="itemValueType" className="block text-sm font-medium text-gray-700 mb-1">
                        Value Type
                      </label>
                      <select
                        id="itemValueType"
                        name="valueType"
                        value={newItem.valueType}
                        onChange={handleNewItemChange}
                        className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                      >
                        <option value="percentage">Percentage</option>
                        <option value="fixed">Fixed Amount</option>
                      </select>
                    </div>
                    
                    <div className="md:col-span-1">
                      <label htmlFor="itemValue" className="block text-sm font-medium text-gray-700 mb-1">
                        Value
                      </label>
                      <input
                        type="number"
                        id="itemValue"
                        name="value"
                        value={newItem.value}
                        onChange={handleNewItemChange}
                        min="0"
                        step="0.01"
                        className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                        placeholder={newItem.valueType === 'percentage' ? '5%' : '$100'}
                      />
                    </div>
                    
                    <div className="md:col-span-1 flex items-end">
                      <button
                        type="button"
                        onClick={handleAddItem}
                        disabled={!newItem.name || !newItem.value}
                        className={`w-full px-4 py-2.5 rounded-lg ${
                          !newItem.name || !newItem.value
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-[#190192] text-white hover:bg-[#2C0DB5]'
                        }`}
                      >
                        <Plus size={16} className="mx-auto" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2">
                    <input
                      type="text"
                      id="itemDescription"
                      name="description"
                      value={newItem.description}
                      onChange={handleNewItemChange}
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                      placeholder="Description (optional)"
                    />
                  </div>
                </div>
                
                {/* Display current deductions and benefits */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-md font-medium text-gray-900 mb-2">Deductions</h3>
                    {formData.customDeductions.length === 0 ? (
                      <p className="text-gray-500 text-sm">No custom deductions added</p>
                    ) : (
                      <div className="space-y-2">
                        {formData.customDeductions.map((deduction, index) => (
                          <div 
                            key={index}
                            className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                          >
                            <div>
                              <div className="font-medium text-gray-900">{deduction.name}</div>
                              <div className="text-sm text-gray-500">
                                {deduction.type === 'percentage' ? `${deduction.value}%` : `$${deduction.value}`}
                                {deduction.description && ` - ${deduction.description}`}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveItem('deduction', index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <h3 className="text-md font-medium text-gray-900 mb-2">Benefits</h3>
                    {formData.customBenefits.length === 0 ? (
                      <p className="text-gray-500 text-sm">No custom benefits added</p>
                    ) : (
                      <div className="space-y-2">
                        {formData.customBenefits.map((benefit, index) => (
                          <div 
                            key={index}
                            className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                          >
                            <div>
                              <div className="font-medium text-gray-900">{benefit.name}</div>
                              <div className="text-sm text-gray-500">
                                {benefit.type === 'percentage' ? `${benefit.value}%` : `$${benefit.value}`}
                                {benefit.description && ` - ${benefit.description}`}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveItem('benefit', index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                placeholder="Any additional notes about this employee's payroll"
              />
            </div>
            
            {/* Submit Button */}
            <div className="flex justify-end space-x-4 border-t border-gray-200 pt-6">
              <button
                type="button"
                onClick={() => navigate('/payroll/employees')}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`px-6 py-2.5 bg-[#190192] text-white font-medium rounded-lg ${
                  submitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#2C0DB5]'
                } flex items-center`}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin h-4 w-4 mr-2 border-2 border-white rounded-full border-t-transparent"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={18} className="mr-2" />
                    {isEditing ? 'Update Payroll' : 'Save Payroll'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  };
  
  export default EmployeePayrollForm;